/**
 * Client Portal API Client
 * ========================
 * Axios instance with automatic JWT token handling and auth redirect.
 * Now supports httpOnly cookie-based authentication.
 * 
 * Usage:
 *   import { apiClient } from '@/lib/clientApi';
 *   const res = await apiClient.get('/api/system-expansion/client-v2/dashboard');
 */

import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  withCredentials: true,  // CRITICAL: Send/receive httpOnly cookies
});

// Attach token automatically to all requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("client_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth failure globally
apiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("client_token");
      window.location.href = "/client-login";
    }
    return Promise.reject(err);
  }
);

/**
 * Client Auth Utilities
 * =====================
 */
export const clientAuth = {
  login: async (email, password) => {
    const res = await apiClient.post("/api/client-auth/login", {
      email,
      password,
    });

    // Backend returns 'token' not 'access_token'
    const token = res.data?.token;
    if (!token) throw new Error("No token returned");

    localStorage.setItem("client_token", token);
    return res.data;
  },

  logout: () => {
    localStorage.removeItem("client_token");
    window.location.href = "/client-login";
  },

  isAuthenticated: () => {
    return !!localStorage.getItem("client_token");
  },

  getToken: () => {
    return localStorage.getItem("client_token");
  },
};

export default apiClient;
